import { CreditCard, Loader2, Mail } from "lucide-react";
import { Button } from "@/frontend/components/ui/button";
import { useBillingStateQuery } from "@/frontend/queries/dashboard/billing";
import { PLAN_CODE, type PlanCode } from "@/server/types/billing";
import { PLAN_LABEL } from "@/frontend/constants/plans";
import { supportMailto } from "@/frontend/utils/support";

/** Dashboard section showing the workspace's current plan and patient usage. Plan and feature changes are handled by support. */
export function UsageSection() {
  const { data: state, isLoading } = useBillingStateQuery();

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Loading usage…
      </div>
    );
  }

  const currentPlan: PlanCode = state?.plan_code ?? PLAN_CODE.FREE;
  const overCap =
    currentPlan === PLAN_CODE.FREE && state
      ? state.active_patient_count >= state.active_patient_limit
      : false;
  const usagePct = state && state.active_patient_limit > 0
    ? Math.min(100, Math.round((state.active_patient_count / state.active_patient_limit) * 100))
    : 0;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-xl font-semibold">Usage</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Active patient count for this workspace. Contact support to change plans or unlock features.
        </p>
      </div>

      {state && (
        <div className="rounded-lg border p-4 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-muted-foreground">Current plan</div>
              <div className="text-lg font-semibold flex items-center gap-2">
                <CreditCard className="size-4" />
                {PLAN_LABEL[currentPlan]}
              </div>
            </div>
            <div className="text-right text-sm">
              <div className="text-muted-foreground">Patients</div>
              <div className="font-medium">
                {state.active_patient_count.toLocaleString()}
                {currentPlan === PLAN_CODE.FREE && (
                  <> / {state.active_patient_limit.toLocaleString()}</>
                )}
              </div>
            </div>
          </div>

          {currentPlan === PLAN_CODE.FREE && (
            <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
              <div
                className={overCap ? "h-full bg-destructive" : "h-full bg-primary"}
                style={{ width: `${usagePct}%` }}
              />
            </div>
          )}

          {overCap && (
            <div className="text-sm text-destructive">
              You've hit your patient limit. Contact support to increase it.
            </div>
          )}
        </div>
      )}

      <div className="rounded-lg border p-4 flex items-center justify-between gap-4">
        <div>
          <div className="text-sm font-medium">Need more patients or a paid feature?</div>
          <p className="text-xs text-muted-foreground mt-1">
            Plan changes and feature unlocks are handled by support.
          </p>
        </div>
        <Button asChild variant="outline">
          <a href={supportMailto("Plan or feature change request")}>
            <Mail className="size-4 mr-2" />
            Contact support
          </a>
        </Button>
      </div>
    </div>
  );
}
